import React, { useState } from "react";
import Modal from "@components/atoms/Modal";
import styled from "styled-components/native";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "@scenes/favorites/utils/collectionSchema";
import { Input, VDBText } from "@components/atoms";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Button } from "@components/atoms";
import { ButtonType, ButtonCategory } from "@components/atoms/Button/types";
import { useAddCollection, useProductDeleteFavorites } from "@apis/collection";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useCollectionListModal } from "@apis/collection";
import { FlatList, Platform, Pressable, View } from "react-native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import Animated, { useAnimatedStyle } from "react-native-reanimated";
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Collection } from "@src/types/CollectionListResponse";
import FooterSpace from "@components/common/FooterSpace";
import { useIsFocused } from "@react-navigation/native";
import { useEffect } from "react";
import { useAddItemsToCollection } from "@apis/collection";
import { useFavoriteCount } from "@apis/collection";
import { css } from "styled-components/native";
import Loader from "@components/atoms/Loader";
import { FavoritesEnum } from "@scenes/favorites/utils/FavoritesUtils";
import CollectionItem from "@scenes/favorites/CollectionItem";
import { CollectionListProps, payLoad } from "./types";
import { useInvalidateContext } from "@scenes/favorites/utils/ContextApi";
import { VDBIcons } from "@utils/VDBIcons";
import { useTheme } from "styled-components/native";
import { DefaultTheme } from "styled-components/native";

const CollectionListScreen = ({
  itemIdArray,
  onClose,
  existingCollectionId,
  favItemIdArray,
  favoriteRemoved,
  collectionItemIdArray,
  collectionRemoved,
  fromSearchResultScreen,
  removeFavoriteItem,
  inWindow,
}: CollectionListProps) => {
  const theme = useTheme();
  const isFocused = useIsFocused();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const insets = useSafeAreaInsets();
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation();
  const isTablet = theme.isTablet || theme.isLargeScreen;
  const [showApiLoader, setShowApiLoader] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [favCheckboxSelected, setFavCheckboxSelected] = useState(
    existingCollectionId === 0
  );
  const [collectionIdList, setCollectionIdList] = useState<number[]>([]);
  const [isCollectionEventLoading, setCollectionEventLoading] = useState(false);
  const idArray = fromSearchResultScreen ? itemIdArray : favItemIdArray;
  const [
    isAddItemToCollectionEventLoading,
    setAddItemToCollectionEventLoading,
  ] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<string>(
    FavoritesEnum.create_new_collection
  );
  const [isCreateNewCollectionModalOpen, setIsCreateNewCollectionModalOpen] =
    useState(false);
  const [enableCollectionButton, setEnableCollectionButton] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  const favoriteCountCall = useFavoriteCount([
    "COLLECTION_LIST_FAVORITE_COUNT",
  ]);
  const removeItemFromFavorites = useProductDeleteFavorites();
  const addItemsToCollection = useAddItemsToCollection(fromSearchResultScreen);
  const pageNumber = 1;
  const pageSize = 99;
  const collectionType = "mix_sort";

  const collectionListCall = useCollectionListModal(
    pageNumber,
    pageSize,
    collectionType
  );
  const addCollection = useAddCollection();

  const checkEnableCollectionButton = (etValue: string) => {
    setEnableCollectionButton(!!etValue);
  };
  const {
    invalidateAndRefetchFavoriteCount,
    invalidateAndRefetchCollectionList,
  } = useInvalidateContext();

  const addItemsToCollectionFunction = () => {
    setAddItemToCollectionEventLoading(true);

    const apiPayLoad: payLoad = {
      stock_item_id: itemIdArray,
      collection_id: collectionIdList,
    };

    if (existingCollectionId && existingCollectionId > 0) {
      apiPayLoad.action = FavoritesEnum.move;
      apiPayLoad.current_collection_id = existingCollectionId;
    }

    addItemsToCollection.mutate(
      { collection: apiPayLoad },
      {
        onSuccess(data: any) {
          handleAddToCollectionSuccess(data);
          if (!favCheckboxSelected && removeFavoriteItem) {
            removeItemsFromFavorites();
          }
          if (isTablet) {
            invalidateAndRefetchCollectionList(pageNumber);
          }
        },
        onSettled: () => setAddItemToCollectionEventLoading(false),
        onError: (data: any) => {
          setTimeout(() => {
            showToast(
              data.response.data.title,
              data.response.data.message,
              ToastTypes.ERROR,
              true,
              true,
              120
            );
          }, 1000);
          if (isTablet) {
            invalidateAndRefetchFavoriteCount();
          }

          onClose();
        },
      }
    );
  };

  const removeItemsFromFavorites = () => {
    removeItemFromFavorites.mutate(
      { id: favItemIdArray[0] },
      {
        onSuccess: () => {
          if (favoriteRemoved) {
            favoriteRemoved(favItemIdArray);
          }
          if (isTablet) {
            invalidateAndRefetchFavoriteCount();
          }
        },
        onSettled: () => setAddItemToCollectionEventLoading(false),
      }
    );
  };

  const handleAddToCollectionSuccess = (data: any) => {
    if (
      existingCollectionId &&
      !collectionIdList.includes(existingCollectionId) &&
      collectionRemoved
    ) {
      collectionRemoved(collectionItemIdArray);
    } else if (
      !favCheckboxSelected &&
      idArray &&
      favoriteRemoved &&
      removeFavoriteItem
    ) {
      favoriteRemoved(idArray);
    }

    if (data.status === 200) {
      setTimeout(() => {
        showToast(
          data.data.title,
          data.data.message,
          ToastTypes.SUCCESS,
          true,
          true,
          120
        );
      }, 500);

      if (isTablet) {
        invalidateAndRefetchFavoriteCount();
      }
    } else if (data.status === 202) {
      showToast(
        data.data.title,
        data.data.message,
        ToastTypes.INFO,
        true,
        true,
        120
      );
    }
    onClose();
  };

  const renderFooter = () => {
    return <FooterSpace isLoader={false} backgroundColor={"core-02"} />;
  };

  useEffect(() => {
    if (isFocused && Platform.OS !== "web") {
      refetchAPIs();
    }
  }, [isFocused]);

  const refetchAPIs = () => {
    setShowApiLoader(true);
    collectionListCall.refetch();
    if (existingCollectionId === 0) {
      favoriteCountCall.refetch();
    }
  };

  useEffect(() => {
    const { data, isSuccess, isFetched, isError } = collectionListCall;
    if (isFetched || isError) {
      setShowApiLoader(false);
    }
    if (isSuccess) {
      const mCollections = data?.data?.collections ?? [];
      setCollections(mCollections);

      if (existingCollectionId) {
        checkboxFunction(true, existingCollectionId, false);
      }
    }
  }, [collectionListCall.data]);

  useEffect(() => {
    const { data, isSuccess } = favoriteCountCall;

    if (isSuccess) {
      const count = data?.data?.data?.count ?? 0;
      setFavoriteCount(count);
    }
  }, [favoriteCountCall.isSuccess, favoriteCountCall.data]);

  const onSubmit = (data: any) => {
    const alteredCollectionName = data.collectionName.trim();

    if (currentEvent === FavoritesEnum.create_new_collection) {
      addCollection.mutate(
        {
          name: alteredCollectionName,
          type: 5,
        },
        {
          onSuccess() {
            setCollectionEventLoading(false);
            setIsCreateNewCollectionModalOpen(false);
            setValue(FavoritesEnum.collection_name, "");
            checkEnableCollectionButton("");
            setTimeout(() => {
              showToast(
                t("global.text.done"),
                t("favorites.newCollectionCreated.text"),
                ToastTypes.SUCCESS,
                true,
                true,
                inWindow ? Math.min(110, insets.bottom + 80) : undefined,
                undefined,
                true,
                4000,
                false,
                0,
                undefined,
                undefined,
                undefined,
                inWindow ? 80 : undefined
              );
            }, 1000);
            refetchAPIs();
          },
        }
      );
    }
  };

  const checkboxFunction = (
    checked: boolean,
    collectionId: number,
    isFavorites: boolean
  ) => {
    if (isFavorites) {
      setFavCheckboxSelected(checked);
    } else {
      setCollectionIdList((prevData) => {
        const updatedSet = new Set(prevData);
        if (checked) {
          updatedSet.add(collectionId);
        } else {
          updatedSet.delete(collectionId);
        }
        return Array.from(updatedSet);
      });
    }
  };

  const createCollectionModalHeaderTitle =
    currentEvent === FavoritesEnum.create_new_collection
      ? t("favorites.createNewCollection.text")
      : t("favorites.renameCollection.text");

  const closeCreateCollectionModal = () => {
    setIsCreateNewCollectionModalOpen(false);
    setValue(FavoritesEnum.collection_name, "");
    checkEnableCollectionButton("");
  };

  const createCollectionModalContent = (
    <>
      <Controller
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <Input
            testID="collection-list-screen-collection-name-input"
            autoFocus={false}
            inputLabel={t("favorites.collectionName.text")}
            placeholder={t("favorites.collectionName.text")}
            hideIconContainer={true}
            onBlur={onBlur}
            onChangeText={(text) => {
              onChange(text.trimStart());
              checkEnableCollectionButton(text.trimStart());
            }}
            value={value}
          />
        )}
        name={FavoritesEnum.collection_name}
      />

      <Spacer size={SpaceType.Big} />

      <Button
        testID="collection-list-screen-create-collection-button"
        loading={isCollectionEventLoading}
        type={ButtonType.primary}
        disabled={!enableCollectionButton}
        category={ButtonCategory.default}
        title={t("collectionDialog.button.createNewCollection.text")}
        onPress={() => {
          setCollectionEventLoading(true);
          handleSubmit(onSubmit)();
        }}
      />

      <Spacer size={SpaceType.Big + SpaceType.ExtraSmall} />
    </>
  );

  // On phone-native the create form is a bottom sheet; stacking it as a third
  // animated RN Modal (over the gallery + collection-list sheets) collapsed the
  // parent, so we render it as an in-window overlay instead. Web and large
  // screens use the shared Modal (a centered popup) which has no such issue.
  const useInWindowOverlay = Platform.OS !== "web" && !theme.isLargeScreen;

  // Lift the bottom-anchored create card above the keyboard. `keyboardHeight`
  // is negative while the keyboard is open; clamped to 0 when closed. Reads the
  // app-root KeyboardProvider's global shared value, which works across the RN
  // Modal boundary without needing a nested provider.
  const createLiftStyle = useAnimatedStyle(() => ({
    paddingBottom:
      Platform.OS === "web"
        ? 0
        : Math.max(-keyboardHeight.value - insets.bottom, 0),
  }));

  return (
    <Container isTablet={isTablet}>
      {!useInWindowOverlay && (
        <Modal
          headerTitle={createCollectionModalHeaderTitle}
          isOpen={isCreateNewCollectionModalOpen}
          onClose={closeCreateCollectionModal}
          paddingLeft={16}
          paddingRight={16}
          paddingBottom={40}
          paddingTop={16}
        >
          {createCollectionModalContent}
        </Modal>
      )}
      <FlatList
        data={collections}
        style={[isTablet ? { height: 500 } : {}]}
        showsHorizontalScrollIndicator={false}
        ListHeaderComponent={() => (
          <>
            {existingCollectionId === 0 && collectionListCall.isFetched ? (
              <CollectionItem
                testID="collection-list-screen-favorites-item"
                isFavorites={true}
                count={favoriteCount}
                showCheckbox={true}
                fromFavoriteScreen={false}
                checkboxFunction={checkboxFunction}
                selectCheckbox={favCheckboxSelected}
                selectedItem={null}
                handleReset={() => {}}
              />
            ) : (
              <View />
            )}
          </>
        )}
        renderItem={({ item }) => (
          <CollectionItem
            testID={`collection-list-screen-collection-item-${item.id}`}
            item={item}
            isFavorites={false}
            count={item.count}
            fromFavoriteScreen={false}
            showCheckbox={true}
            checkboxFunction={checkboxFunction}
            selectCheckbox={item.id === existingCollectionId}
            selectedItem={null}
            handleReset={() => {}}
          />
        )}
        keyExtractor={({ id }: Collection) => id + ""}
        ListFooterComponent={renderFooter}
        contentContainerStyle={{ paddingBottom: 50 }}
      />
      {!showApiLoader && (
        <ButtonContainer>
          <Button
            testID="collection-list-screen-add-to-collection-button"
            loading={isAddItemToCollectionEventLoading}
            type={ButtonType.primary}
            category={ButtonCategory.default}
            title={t("collectionDialog.button.addItemToCollection.text", {
              x: itemIdArray.length > 1 ? itemIdArray.length : "",
            })}
            disabled={collectionIdList.length === 0}
            onPress={() => {
              addItemsToCollectionFunction();
            }}
          />
          <Spacer size={SpaceType.Double} />
          <Button
            testID="collection-list-screen-open-create-collection-button"
            type={ButtonType.secondary}
            category={ButtonCategory.default}
            title={t("collectionDialog.button.createNewCollection.text")}
            textTypographyStyle={"H3-M"}
            onPress={() => {
              setIsCreateNewCollectionModalOpen(true);
              setCurrentEvent(FavoritesEnum.create_new_collection);
            }}
            showIcon={true}
            iconSource={VDBIcons.ADD_CIRCLE}
            iconSize={24}
          />
        </ButtonContainer>
      )}
      {showApiLoader && (
        <Loader backgroundColor={theme["core-02"]} opacity={0.8} />
      )}
      {useInWindowOverlay && isCreateNewCollectionModalOpen && (
        <CreateOverlay style={createLiftStyle}>
          <CreateBackdrop onPress={closeCreateCollectionModal} />
          <CreateCard>
            <CreateHeaderStrip>
              <VDBText
                textAlign="center"
                type={typography["H2-M"]}
                color={theme["core-05"]}
              >
                {createCollectionModalHeaderTitle}
              </VDBText>
              <CreateCloseButton
                testID="collection-list-screen-create-collection-close"
                onPress={closeCreateCollectionModal}
              >
                <VDBIcon
                  name={VDBIcons.CLOSE_CIRCLE}
                  variant={IconsVariant.VDBCustom}
                  size={24}
                  color={theme["core-06"]}
                />
              </CreateCloseButton>
            </CreateHeaderStrip>
            <CreateDivider />
            <CreateBody>{createCollectionModalContent}</CreateBody>
          </CreateCard>
        </CreateOverlay>
      )}
    </Container>
  );
};

const Container = styled.View<any>`
  flex: 1;
  ${({ isTablet }: { isTablet: boolean }) =>
    isTablet &&
    css`
      flex-basis: fit-content;
      width: ${Platform.OS === "web" ? "100%" : "608px"};
      align-self: center;
    `}
  ${Platform.OS === "web" &&
  css`
    width: 100%;
    align-self: center;
    min-height: 450px;
  `}
`;

const ButtonContainer = styled.View`
  position: absolute;
  bottom: 0px;
  left: 0px;
  right: 0px;
  margin-start: 8px;
  margin-end: 8px;
  padding: 10px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  z-index: 100;
`;

// The create-collection form is rendered as an in-window overlay (not a nested
// RN Modal). Stacking a third animated bottom-sheet Modal over the gallery +
// collection-list modals made the parent sheet collapse, so we keep it in the
// same window and lift it above the keyboard via the app-root keyboard value.
// The negative insets cancel the parent Modal's content padding (paddingLeft/
// Right={16}, paddingTop={16}, paddingBottom={32} in CollectionModal) so the
// sheet renders edge-to-edge like a normal bottom-sheet modal.
const CreateOverlay = styled(Animated.View)`
  position: absolute;
  top: -16px;
  left: -16px;
  right: -16px;
  bottom: -32px;
  z-index: 300;
  elevation: 30;
  justify-content: flex-end;
`;

const CreateBackdrop = styled(Pressable)`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const CreateCard = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  shadow-color: #000;
  shadow-offset: 0px -3px;
  shadow-opacity: 0.15;
  shadow-radius: 8px;
  elevation: 16;
`;

const CreateHeaderStrip = styled.View`
  position: relative;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 16px;
`;

const CreateDivider = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const CreateBody = styled.View`
  padding: 16px 16px 40px 16px;
`;

const CreateCloseButton = styled.TouchableOpacity`
  position: absolute;
  right: 12px;
`;

export default CollectionListScreen;
